// src/ConsumerApp/Screens/Account/views/PoliciesView.js

import React, { useState } from 'react';
import { theme } from '../../../../assets/theme/theme';

// Real SVGs from assets/icons
import FileTextIcon from '../../../../assets/icons/FileText.svg';
import ShieldIcon from '../../../../assets/icons/profileStatus/ShieldCheck.svg';
import XCircleIcon from '../../../../assets/icons/XCircleIcon.svg';
import ChevronRightIcon from '../../../../assets/icons/CaretRight.svg';
import ExternalLinkIcon from '../../../../assets/icons/ExternalLink.svg';
import LogoBB from '../../../../assets/icons/logoBB.svg';

const POLICY_ITEMS = [
  {
    id: 'about',
    title: 'About Us',
    subText: 'Learn more about Brickbucket and our procurement network',
    icon: FileTextIcon,
  },
  {
    id: 'terms',
    title: 'Terms & Conditions',
    subText: 'Platform rules, buyer responsibilities, and operational guidelines',
    icon: FileTextIcon,
  },
  {
    id: 'privacy',
    title: 'Privacy Policy',
    subText: 'How we collect, protect, and manage your data',
    icon: ShieldIcon,
  },
  {
    id: 'cancellation',
    title: 'Cancellation & Refund Policy',
    subText: 'Rules and timelines for order cancellations and replacements',
    icon: XCircleIcon,
  },
];

// Content ported from mobile policy screens
const ABOUT_US_CONTENT = {
  title: 'About Us',
  subtitle: 'Learn more about Brickbucket',
  intro:
    'Brickbucket is a technology-enabled platform operated by Brickbucket Build Solutions Pvt Ltd., focused on simplifying the procurement of construction materials.',
  sections: [
    {
      title: '1. WHAT WE DO',
      body: 'We operate as a reseller, sourcing construction materials from trusted suppliers and selling them directly to customers through our digital platform. Our goal is to make material procurement more transparent, efficient, and reliable for individual builders and small businesses.',
      bullets: null,
    },
    {
      title: '2. PLATFORM CAPABILITIES',
      body: 'Brickbucket enables users to:',
      bullets: [
        'Discover construction materials across categories',
        'View live pricing based on their structural requirements',
        'Place orders through a simple and structured process',
        'Receive direct site deliveries through third-party logistics partners',
      ],
    },
    {
      title: '3. OUR OPERATIONS & MISSION',
      body: 'We work with a network of suppliers and logistics providers to ensure timely fulfillment and consistent service quality.\n\nOur mission is to bring efficiency and standardization to construction material procurement through a streamlined digital experience.',
      bullets: null,
    },
    {
      title: '4. CONTACT',
      body: 'Email: admin@brickbucket.in\nPhone: +91-8247895454',
      bullets: null,
    },
  ],
  version: 'Brickbucket Platform v4 (Web Edition)',
};

const TERMS_CONTENT = {
  title: 'Terms & Conditions',
  subtitle: 'Last updated on May 07, 2026',
  intro:
    'Welcome to Brickbucket. These Terms & Conditions (“Terms”) govern your use of the Brickbucket digital applications and procurement services. This platform is owned and operated by Brickbucket Build Solutions Pvt Ltd. By accessing or using the platform, you agree to be bound by these Terms.',
  sections: [
    {
      title: '1. BUSINESS MODEL',
      body: 'Brickbucket operates as a reseller of construction materials and related building products.',
      bullets: [
        'All products listed on the platform are sold directly by Brickbucket to users.',
        'Brickbucket may procure such products from vetted third-party manufacturers and suppliers.',
        'Delivery is fulfilled through third-party logistics providers directly to designated sites.',
      ],
    },
    {
      title: '2. USER ELIGIBILITY',
      body: null,
      bullets: [
        'You must be at least 18 years old to transact on the platform.',
        'You agree to provide accurate, current, and complete corporate or individual billing details.',
        'You are responsible for maintaining account credential confidentiality.',
      ],
    },
    {
      title: '3. ORDERS & PRICING',
      body: null,
      bullets: [
        'Prices displayed are determined by Brickbucket and may vary based on product specifications, quantities, and site delivery geo-distance.',
        'Prices are subject to final adjustment until the order is confirmed.',
        'Orders are confirmed only after successful payment or approved credit terms.',
        'Once confirmed, orders cannot be modified except at Brickbucket’s operational discretion.',
      ],
    },
    {
      title: '4. PAYMENTS',
      body: null,
      bullets: [
        'Payments must be made through authorized gateway methods (UPI, Net Banking, Cards, NEFT/RTGS).',
        'Brickbucket uses secure third-party payment gateways powered by Cashfree/authorized aggregators.',
        'You agree to pay all applicable charges including product price, GST, delivery charges, and unloading fees.',
      ],
    },
    {
      title: '5. DELIVERY & LOGISTICS',
      body: null,
      bullets: [
        'Deliveries are fulfilled through third-party commercial transport partners.',
        'Delivery timelines (e.g., 30-90 mins for Fast Delivery) are indicative and contingent on traffic and weather.',
        'Risk and title of goods pass to the User upon physical site receipt and unloading.',
      ],
    },
    {
      title: '6. CANCELLATION, RETURNS & REFUNDS',
      body: null,
      bullets: [
        'Orders once dispatched may not be cancelled except at Brickbucket’s operational discretion.',
        'Returns or replacements are allowed only for damaged, defective, or incorrect products, subject to verification.',
        'Issues must be reported within the timeline defined in the Refund Policy (48 hours).',
      ],
    },
    {
      title: '7. CONTACT',
      body: 'Email: admin@brickbucket.in\nPhone: +91-8247895454',
      bullets: null,
    },
  ],
};

const PRIVACY_CONTENT = {
  title: 'Privacy Policy',
  subtitle: 'Last updated on May 07, 2026',
  intro:
    'This Privacy Policy explains how Brickbucket Build Solutions Pvt Ltd. (“Brickbucket”) collects, uses, and protects your information. By using the platform, you consent to these practices.',
  externalLink: 'https://brickbucket.in/Privacy-Policy.html',
  sections: [
    {
      title: '1. INFORMATION WE COLLECT',
      body: null,
      bullets: [
        'Corporate identity, individual name, phone number, GSTIN, and email address',
        'Site delivery addresses and GPS coordinate data',
        'Order histories, bill totals, and commercial transaction logs',
        'Device, browser environment, and application usage patterns',
      ],
    },
    {
      title: '2. USE OF INFORMATION',
      body: 'We use your data to:',
      bullets: [
        'Process, verify, and fulfill material dispatches',
        'Facilitate secure payment processing and tax invoicing',
        'Coordinate logistics and site unloading schedules',
        'Provide responsive customer helpline and order tracking support',
      ],
    },
    {
      title: '3. PAYMENTS & BANKING DATA',
      body: 'All online payments are processed through PCI-DSS compliant payment gateways. Brickbucket never stores full card numbers, CVVs, or UPI PINs.',
      bullets: null,
    },
    {
      title: '4. DATA SHARING',
      body: 'We share necessary order parameters strictly with trusted delivery partners, material distribution hubs, and regulatory authorities where legally mandated.',
      bullets: null,
    },
    {
      title: '5. CONTACT',
      body: 'Email: admin@brickbucket.in\nPhone: +91-8247895454',
      bullets: null,
    },
  ],
};

const REFUND_CONTENT = {
  title: 'Cancellation & Refund Policy',
  subtitle: 'Last updated on May 07, 2026',
  intro:
    'Brickbucket platform guidelines for procurement order cancellations, defective replacements, and credit returns.',
  sections: [
    {
      title: '1. ORDER CANCELLATION',
      body: null,
      bullets: [
        'Instant orders are confirmed immediately upon successful payment verification.',
        'Once loaded onto vehicles or confirmed at supplier yards, orders cannot be cancelled except under Brickbucket operational discretion.',
      ],
    },
    {
      title: '2. ELIGIBLE CASES FOR RETURN / REPLACEMENT',
      body: 'Returns or replacements are only applicable under the following conditions:',
      bullets: [
        'Damaged or broken materials during transit',
        'Defective or sub-standard product batches',
        'Incorrect material grades or items unloaded at site',
      ],
    },
    {
      title: '3. REPORTING TIMELINE',
      body: null,
      bullets: [
        'Delivery discrepancies must be photographed and reported within 48 hours of unloading.',
        'Claims filed after 48 hours cannot be reconciled with supplier yards.',
      ],
    },
    {
      title: '4. REFUND PROCESS',
      body: null,
      bullets: [
        'Approved refunds are credited directly to your Brickbucket Wallet or original payment instrument.',
        'Banking and gateway timelines typically take 3 to 5 business days to credit.',
      ],
    },
    {
      title: '5. NON-ELIGIBLE CASES',
      body: 'Refunds or replacements are not provided for:',
      bullets: [
        'Change of mind or site requirement reduction after loading',
        'Incorrect structural estimation inputs provided by the user',
        'Normal minor variations in natural stone or masonry appearances',
      ],
    },
    {
      title: '6. CONTACT',
      body: 'Email: admin@brickbucket.in\nPhone: +91-8247895454',
      bullets: null,
    },
  ],
};

export default function PoliciesView() {
  const [activePolicyId, setActivePolicyId] = useState('about');

  const activeContent =
    activePolicyId === 'about'
      ? ABOUT_US_CONTENT
      : activePolicyId === 'terms'
      ? TERMS_CONTENT
      : activePolicyId === 'privacy'
      ? PRIVACY_CONTENT
      : REFUND_CONTENT;

  return (
    <div style={styles.container}>
      {/* ── Page Header ── */}
      <div style={styles.pageHeader}>
        <div>
          <h2 style={styles.headerTitle}>Legal & Policies</h2>
          <span style={styles.headerSubtitle}>
            Review operational terms, procurement policies, and company background
          </span>
        </div>
      </div>

      {/* ── Desktop Split: Policy Selector & Detailed Document Viewer ── */}
      <div style={styles.policySplit}>
        {/* Left Mini-Nav: Policy Card Stack */}
        <div style={styles.policyNavStack}>
          {POLICY_ITEMS.map((item) => {
            const isSelected = activePolicyId === item.id;
            const Icon = item.icon;

            return (
              <div
                key={item.id}
                style={{
                  ...styles.policySelectorCard,
                  borderColor: isSelected ? theme.colors.brand : '#E2E8F0',
                  backgroundColor: isSelected ? '#FFF8F8' : '#FFFFFF',
                }}
                onClick={() => setActivePolicyId(item.id)}
              >
                <div
                  style={{
                    ...styles.policyIconBox,
                    backgroundColor: isSelected ? '#FEE2E2' : '#F8FAFC',
                  }}
                >
                  <img
                    src={Icon}
                    alt={item.title}
                    style={{
                      width: 18,
                      height: 18,
                      filter: isSelected
                        ? 'invert(16%) sepia(85%) saturate(6120%) hue-rotate(348deg) brightness(91%) contrast(106%)'
                        : 'none',
                    }}
                  />
                </div>

                <div style={styles.policySelectorText}>
                  <strong
                    style={{
                      ...styles.policySelectorTitle,
                      color: isSelected ? theme.colors.brand : '#2B2A29',
                    }}
                  >
                    {item.title}
                  </strong>
                  <span style={styles.policySelectorSub}>{item.subText}</span>
                </div>

                <img
                  src={ChevronRightIcon}
                  alt="›"
                  style={{
                    width: 14,
                    height: 14,
                    opacity: isSelected ? 0.9 : 0.35,
                    filter: isSelected
                      ? 'invert(16%) sepia(85%) saturate(6120%) hue-rotate(348deg) brightness(91%) contrast(106%)'
                      : 'none',
                  }}
                />
              </div>
            );
          })}
        </div>

        {/* Right Reader: Rendered Policy Document */}
        <div style={styles.documentViewer}>
          <div style={styles.docHeaderRow}>
            <div style={styles.docLogoWrap}>
              <img src={LogoBB} alt="Brickbucket" style={styles.docLogoImg} />
            </div>

            <div>
              <h3 style={styles.docTitle}>{activeContent.title}</h3>
              <span style={styles.docSubtitle}>{activeContent.subtitle}</span>
            </div>
          </div>

          <div style={styles.divider} />

          <p style={styles.docIntro}>{activeContent.intro}</p>

          <div style={styles.sectionsList}>
            {activeContent.sections.map((section, idx) => (
              <div key={idx} style={styles.docSection}>
                <h4 style={styles.sectionHeading}>{section.title}</h4>

                {section.body && (
                  <p style={styles.sectionBodyText}>{section.body}</p>
                )}

                {section.bullets && (
                  <ul style={styles.bulletList}>
                    {section.bullets.map((bullet, bIdx) => (
                      <li key={bIdx} style={styles.bulletItem}>
                        {bullet}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>

          {activeContent.externalLink && (
            <div style={styles.externalLinkRow}>
              <a
                href={activeContent.externalLink}
                target="_blank"
                rel="noopener noreferrer"
                style={styles.externalLinkBtn}
              >
                <img src={ExternalLinkIcon} alt="link" style={{ width: 14, height: 14 }} />
                <span>View Full Document on Brickbucket Website</span>
              </a>
            </div>
          )}

          {activeContent.version && (
            <div style={styles.docFooter}>
              <span style={styles.versionTag}>{activeContent.version}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    width: '100%',
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottom: '1px solid #E2E8F0',
    paddingBottom: 16,
  },
  headerTitle: {
    margin: 0,
    fontSize: 22,
    fontWeight: '800',
    color: '#2B2A29',
  },
  headerSubtitle: {
    fontSize: 13,
    color: '#707B8C',
    marginTop: 4,
    display: 'block',
  },
  policySplit: {
    display: 'grid',
    gridTemplateColumns: '300px 1fr',
    gap: 20,
    alignItems: 'flex-start',
  },
  policyNavStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  policySelectorCard: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderRadius: 12,
    padding: '12px 14px',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  policyIconBox: {
    width: 36,
    height: 36,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  policySelectorText: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  policySelectorTitle: {
    fontSize: 13.5,
    fontWeight: '700',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  policySelectorSub: {
    fontSize: 11,
    color: '#707B8C',
    lineHeight: '15px',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  documentViewer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    border: '1px solid #CBD5E1',
    padding: '28px 32px',
    display: 'flex',
    flexDirection: 'column',
  },
  docHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  docLogoWrap: {
    width: 100,
    height: 36,
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  docLogoImg: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  docTitle: {
    margin: 0,
    fontSize: 18,
    fontWeight: '700',
    color: '#2B2A29',
  },
  docSubtitle: {
    fontSize: 12,
    color: '#707B8C',
    marginTop: 2,
    display: 'block',
  },
  divider: {
    height: 1,
    backgroundColor: '#E2E8F0',
    margin: '20px 0',
  },
  docIntro: {
    margin: '0 0 24px 0',
    fontSize: 13.5,
    color: '#475569',
    lineHeight: '22px',
  },
  sectionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  docSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  sectionHeading: {
    margin: 0,
    fontSize: 13.5,
    fontWeight: '700',
    color: '#2B2A29',
    letterSpacing: '0.4px',
  },
  sectionBodyText: {
    margin: 0,
    fontSize: 13,
    color: '#475569',
    lineHeight: '21px',
    whiteSpace: 'pre-line',
  },
  bulletList: {
    margin: '4px 0 0 0',
    paddingLeft: 20,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  bulletItem: {
    fontSize: 13,
    color: '#475569',
    lineHeight: '20px',
  },
  externalLinkRow: {
    marginTop: 24,
    paddingTop: 16,
    borderTop: '1px dashed #E2E8F0',
  },
  externalLinkBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#FFF1F2',
    color: '#C7061F',
    border: '1px solid #FECDD3',
    padding: '8px 16px',
    borderRadius: 8,
    fontSize: 12.5,
    fontWeight: '600',
    textDecoration: 'none',
  },
  docFooter: {
    marginTop: 28,
    paddingTop: 16,
    borderTop: '1px solid #F1F5F9',
    textAlign: 'center',
  },
  versionTag: {
    fontSize: 11,
    color: '#94A3B8',
    letterSpacing: '1px',
    textTransform: 'uppercase',
  },
};